import { useEffect, useRef, useState } from 'react';
import { Search } from '../../lib/icons.tsx';
import { showingLine } from './activeFilterChips.ts';

/**
 * The decks page's search: a persistent full-width bar under the title.
 *
 * It used to be a magnifier in the app header that eased open on click, and
 * everything clever about that control existed to make a 40px circle become a
 * text field without the bar moving. None of it survives the move: the bar has
 * its own row now, it is always open, and there is nothing to animate. What
 * DOES carry over is why the header keeps its own search on Library and Bank —
 * `AppHeader`'s `bar` slot is per-surface, so this page simply stops passing
 * one.
 *
 * ⌘K focuses it from anywhere on the page, which is only safe because the two
 * guards below hold: never while the keystroke is going into some other field,
 * and never while a dialog is on top. Both are checked against the live DOM
 * rather than against a list of open-modal booleans, so a modal added next year
 * is covered without anyone remembering this file.
 */

type Props = {
  query: string;
  onQuery: (query: string) => void;
  /** How many decks the current view shows, for the polite announcement. */
  resultCount: number;
  /** How many there are in total. */
  totalCount: number;
  /**
   * The four things that name the collection rather than describe the control.
   * All defaulted to the decks page's own words, so its call and its markup are
   * unchanged; the Library passes its own set rather than owning a second copy
   * of the ⌘K handling, the keycap hint and the polite announcement.
   */
  label?: string;
  placeholder?: string;
  /** Unique per page: two search bars on one screen would share an id. */
  inputId?: string;
  /** How the settled result is announced. */
  announce?: (shown: number, total: number) => string;
};

/** Mac shows ⌘, everything else Ctrl. Read once — a person does not change
 *  platform mid-session, and `navigator` is absent under the test runner. */
function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return true;
  return /mac|iphone|ipad|ipod/i.test(navigator.userAgent || '');
}

/** Long enough that a typist hears one result, not one per letter. */
const ANNOUNCE_DELAY_MS = 800;

export default function DeckSearchBar({
  query,
  onQuery,
  resultCount,
  totalCount,
  label = 'Search decks',
  placeholder = 'Search decks by name, slug, folder or model…',
  inputId = 'deck-search-input',
  announce = showingLine,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [isMac] = useState(isMacPlatform);

  // The keycaps are a hint, not a control: once you are typing you already know
  // where the field is, and the shortcut you have just used is clutter.
  const showKeys = !focused && query === '';

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'k' && e.key !== 'K') return;
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      // Typing somewhere else wins. ⌘K is a text shortcut in plenty of fields
      // (a link in a rich editor, a kill-line in a readline-style input), and
      // stealing focus mid-sentence is worse than not having the shortcut.
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      if (target && /^(input|textarea|select)$/i.test(target.tagName)) return;
      // A dialog on top owns the keyboard. Every modal and drawer on this page
      // carries `role="dialog"` — including the header's phone sheet — so one
      // query covers the lot.
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault(); // Firefox aims ⌘K at the address bar otherwise
      // The bar scrolls away under the decks page's sticky filter band, so the
      // shortcut has to BRING IT BACK as well as focus it — focusing an
      // off-screen field silently steals the keyboard. Centred rather than
      // aligned to the top, which would park it behind the band, and instant
      // rather than smooth, which is one fewer thing to guard for reduced
      // motion.
      inputRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // A screen reader should hear the result of a search, not a running commentary
  // on it, so the count settles before it is announced. The visible "Showing N
  // of M" chip-row line is deliberately not the live region for the same reason.
  useEffect(() => {
    if (!query) {
      setAnnouncement('');
      return;
    }
    const id = setTimeout(() => setAnnouncement(announce(resultCount, totalCount)), ANNOUNCE_DELAY_MS);
    return () => clearTimeout(id);
  }, [query, resultCount, totalCount, announce]);

  return (
    <div className={'deck-search' + (focused ? ' is-focused' : '')}>
      <span className="deck-search__icon" aria-hidden="true">
        <Search size={19} />
      </span>
      <input
        ref={inputRef}
        id={inputId}
        className="deck-search__input"
        type="search"
        value={query}
        aria-label={label}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          // Clear and step away. The browser already clears a `type="search"`
          // on Escape; blurring as well is what makes the key a way OUT of the
          // bar rather than a way to sit in an empty one.
          if (e.key === 'Escape') {
            onQuery('');
            e.currentTarget.blur();
          }
        }}
      />
      {showKeys && (
        <span className="deck-search__keys" aria-hidden="true">
          <kbd className="deck-search__key">{isMac ? '⌘' : 'Ctrl'}</kbd>
          <kbd className="deck-search__key">K</kbd>
        </span>
      )}
      <span className="deck-search__status" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
